/**
 * Gate for screens that cannot do anything useful without a project.
 *
 * Renders `children(projectId)` only once a project the user may access is
 * resolved; otherwise it explains which of the several "no project" situations
 * applies and offers a way out. Screens must not call the requirement APIs in
 * any of those states — that is the whole point of funnelling the id through
 * here rather than defaulting it to 1.
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import { Link, Progress } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { useActiveProject } from './ActiveProjectContext';
import { ProjectSelector } from './ProjectSelector';

const useStyles = makeStyles(theme => ({
  state: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  message: {
    marginBottom: theme.spacing(2),
  },
}));

export interface RequireActiveProjectProps {
  children: (projectId: number) => ReactNode;
}

export const RequireActiveProject = ({
  children,
}: RequireActiveProjectProps) => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const { status, activeProjectId, reload } = useActiveProject();

  if (status === 'ready' && activeProjectId !== null) {
    return <>{children(activeProjectId)}</>;
  }

  if (status === 'loading') {
    return <Progress />;
  }

  if (status === 'error') {
    return (
      <Box className={classes.state}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {t('activeProject.retry')}
            </Button>
          }
        >
          {t('activeProject.loadError')}
        </Alert>
      </Box>
    );
  }

  if (status === 'empty') {
    return (
      <Box className={classes.state}>
        <Typography variant="body1" className={classes.message}>
          {t('activeProject.noProjects')}
        </Typography>
        <Link to="/projects">{t('activeProject.goToProjects')}</Link>
      </Box>
    );
  }

  // 'denied' — an id was asked for (URL or storage) that the backend did not
  // offer. Treated as "pick another one", not as a hard error: the usual cause
  // is a stale link or a project that was removed.
  return (
    <Box className={classes.state}>
      <Typography variant="body1" className={classes.message}>
        {status === 'denied'
          ? t('activeProject.denied')
          : t('activeProject.selectPrompt')}
      </Typography>
      <ProjectSelector />
    </Box>
  );
};

export default RequireActiveProject;
